import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	DoCheck,
	EventEmitter,
	Input,
	NgZone,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, pluck, skip, switchMap } from 'rxjs/operators';
import $ from 'jquery';
import { TranslateService } from '@ngx-translate/core';
import { StateService, TransitionService } from '@uirouter/angular';
import { VideogularComponent, VBrickPlayerComponent } from '@vbrick/vbrick-player';

import { DialogService } from 'rev-shared/ui/dialog/Dialog.Service';
import { ISidebarToggleEvent } from 'rev-shared/ui/sidebarLayout/VbUiSidebar.Component';
import { IUnsubscribe } from 'rev-shared/push/IUnsubscribe';
import { IWebcastTranslationKeys } from 'rev-shared/webcast/IWebcastTranslationKeys';
import { LoginRedirectService } from 'rev-shared/security/LoginRedirect.Service';
import { MaxWidthSmallMediaQuery } from 'rev-shared/ui/size/Size.Constants';
import { MediaQueryService } from 'rev-shared/ui/size/MediaQuery.Service';
import { PushBus } from 'rev-shared/push/PushBus.Service';
import { SecondMs } from 'rev-shared/date/Time.Constant';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { VIDEO_OVERLAY_TYPE } from 'rev-shared/videoPlayer/VideoOverlay';
import { WEBCAST_SIDEBAR_NOTIFICATION_EVENT, SidebarNotification } from 'rev-shared/ui/sidebarLayout/SidebarNotification';
import { WebRtcListenerConnectionService } from 'rev-shared/webrtc/WebRtcListenerConnection.Service';
import { WebcastStatus } from 'rev-portal/scheduledEvents/webcast/WebcastStatus';
import { WebcastVideoSource } from 'rev-portal/scheduledEvents/webcast/WebcastVideoSource';
import { noop } from 'rev-shared/util';
import { fadeInOutAnimation } from 'rev-shared/animation/fadeInOut';
import { isMobile } from 'rev-shared/util/UserAgentUtil';
import { AnalyticsViewContext } from 'rev-shared/analytics/Constant';

import { INotifications } from 'rev-portal/scheduledEvents/webcast/Webcast.Contract';
import { MainHeaderPositionService } from 'rev-portal/navigation/mainHeader/MainHeaderPosition.Service';
import { WEBCAST_LANDING_STATE, WEBCAST_VIEW_STATE, TRANSLATION_KEYS } from 'rev-portal/scheduledEvents/webcast/Constants';
import { WebcastModel } from 'rev-portal/scheduledEvents/webcast/model/WebcastModel';
import { WebcastService } from 'rev-portal/scheduledEvents/webcast/Webcast.Service';
import { IEmbeddedContentLink } from 'rev-portal/scheduledEvents/common/EmbeddedContentTypes';
import { AttendeesListService } from 'rev-portal/scheduledEvents/webcast/attendees/AttendeesList.Service';

import { ProducerViewComponent } from './producer/ProducerView.Component';
import { WebcastViewSidebarButton } from './WebcastViewSidebarButtonModel';
import { IWebcastViewContext } from './IWebcastViewContext';
import { WebcastViewSidebarPanelComponent } from './WebcastViewSidebarPanel.Component';
import { WebcastViewService } from './WebcastView.Service';

import styles from './VbWebcastView.Component.module.less';
import 'rev-less/events/webcast-main.less';
import 'rev-less/video/video-main.less'; //refactored player related css later.

const NOTIFICATIONS_SIDEBARS = [
	WebcastViewSidebarButton.CHAT,
	WebcastViewSidebarButton.POLLS,
	WebcastViewSidebarButton.QA
];

@Component({
	selector: 'vb-webcast-view',
	templateUrl: './VbWebcastView.Component.html',
	animations: [
		fadeInOutAnimation
	],
	standalone: false
})
export class VbWebcastViewComponent implements OnInit, OnDestroy, DoCheck, AfterViewInit {
	@Input() public accountId: string;
	@Input() public analyticsViewContext: AnalyticsViewContext;
	@Input() public webcast: WebcastModel;
	@Input() public sidebarNotifications: INotifications;
	@Input() public webcastViewContext: IWebcastViewContext;
	@Input() public translationKeys: IWebcastTranslationKeys;
	@Input() public previousState: { name: string; params: any; };
	@Output() public onPlayerReady: EventEmitter<{ vgAPI: VideogularComponent, player: VBrickPlayerComponent }> = new EventEmitter<any>();
	public readonly styles = styles;
	public readonly WebcastViewSidebarButton = WebcastViewSidebarButton;
	public readonly WebcastVideoSource = WebcastVideoSource;

	public activeSidebarButtonId: string;
	public isSmallScreenSize: boolean;
	public isMobile = isMobile();
	public status: { [key: string]: boolean };
	public viewContext: IWebcastViewContext;
	public inlineEdit: boolean;
	public statusMsg: string;
	public statusClass: string;
	public thumbnailHeight: number;
	public thumbnailWidth: number;
	public showPresenterStatus: boolean;
	public editWebcastInvalid: boolean;
	public attendeeCount$: Observable<number>;
	public isAttendeeListPollingActive: boolean = true;

	private pushSubscription: Subscription;
	private webcastSubscription: Subscription;
	private mediaQuerySubscription: Subscription;
	private readonly rxjsSubscriptions: Subscription[] = [];
	private readonly locationChangeSub: Subscription;
	private editWebcastStatusSub: Subscription;
	private transitionStartHandler: () => void;
	private vcFailed: boolean;
	private embeddedContentNotifications: { [id: string]: SidebarNotification; } = {};

	@ViewChild(ProducerViewComponent)
	private readonly producerView: ProducerViewComponent;

	@ViewChild(WebcastViewSidebarPanelComponent)
	public sidebar: WebcastViewSidebarPanelComponent;

	constructor(
		private readonly $state: StateService,
		private readonly $transitions: TransitionService,
		private readonly changeDetector: ChangeDetectorRef,
		private readonly zone: NgZone,
		private readonly DialogService: DialogService,
		private readonly LoginRedirectService: LoginRedirectService,
		private readonly PushBus: PushBus,
		private readonly translateService: TranslateService,
		private readonly WebcastService: WebcastService,
		private readonly MainHeaderPosition: MainHeaderPositionService,
		private readonly MediaQueryService: MediaQueryService,
		private readonly UserContext: UserContextService,
		private readonly ListenerConnection: WebRtcListenerConnectionService,
		private readonly WebcastViewService: WebcastViewService,
		private readonly AttendeesListService: AttendeesListService
	){}

	public ngOnInit(): void {
		this.initialize();
		this.initVideoOverlays();
		this.WebcastViewService.initWebcastModelObservable(this.webcast);
		this.webcastSubscription = this.webcast.webcast$
			.subscribe(() => {
				this.updateAdminHeader();
			});

		if (this.isMobile && this.webcast.currentUser.isPresenter && this.webcast.isProducerEvent) {
			this.webcast.currentUser.isPresenter = false;
			const dialog = this.DialogService.openStorysetDialog(
				this.translateService.instant('Event_Producer_Setup_Hello'),
				this.translateService.instant('Event_Producer_Setup_MobileBlock'),
				this.translateService.instant('Continue'),
				'/img/mobile-block.png'
			).result;
		}

		if(this.webcast.isWebrtcSinglePresenter &&
			this.webcast.currentUser.isEventAdmin &&
			!this.webcast.currentUser.isSinglePresenter) {
			let timer: number;
			this.webcastSubscription.add(
				this.webcast.webcast$.pipe(
					map(w => w.isPresenterReady),
					distinctUntilChanged(),
					skip(1)
				).subscribe(() => this.zone.run(() =>{
					window.clearTimeout(timer);
					this.showPresenterStatus = true;
					timer = window.setTimeout(() => this.showPresenterStatus = false, 4 * SecondMs);
				})));
		}

		this.mediaQuerySubscription = this.MediaQueryService.getObservable(MaxWidthSmallMediaQuery)
			.subscribe(isSmallScreenSize => {
				this.isSmallScreenSize = isSmallScreenSize;
			});

		this.webcastSubscription.add(this.WebcastService.getChatBlockedUsers(this.webcast).subscribe());

		this.rxjsSubscriptions.push(this.webcastSubscription, this.mediaQuerySubscription);
		this.attendeeCount$ = this.AttendeesListService.getAttendeeCount(this.webcast.id, this.webcast.currentRun.runNumber);
	}
	public ngAfterViewInit(): void {
		setTimeout(() => this.configureNavigationHandlers());
	}
	public ngOnDestroy(): void {
		this.removeNavigationHandlers();
		this.pushSubscription?.unsubscribe();
		this.removeSidebarNotificationEventHandler();
		this.rxjsSubscriptions.forEach(subs => subs?.unsubscribe());
	}

	public ngDoCheck(): void {
		const webcast = this.webcast;
		const vcStatus = webcast.vcStatus;

		if(!this.vcFailed &&
			webcast.currentUser.isEventAdmin &&
			(vcStatus.recordingFailed || vcStatus.connectionFailed) ) {

			this.vcFailed = true;
			console.log('Webcast vc failed');
			this.onWebcastStopped();
		}
		if(!this.editWebcastStatusSub) {
			this.tryInitEditWebcast();
		}
	}

	public leaveWebcast(): void {
		const canControlEventStatus: boolean = this.webcast.currentUser.canControlEventStatus;
		const msg = this.translateService.instant(
			canControlEventStatus ?
				this.webcast.isVodSource ?
					'Event_EndWebcastVodWarning' :
					'Event_EndWebcastWarning' :
				'Event_LeaveWebcastWarning'
		);

		this.DialogService.openConfirmationDialog({
			title: this.translateService.instant('Event_EndEvent'),
			message: this.translateService.instant(msg),
			actionText: this.translateService.instant('Ok'),
			cancelText: this.translateService.instant('Cancel')
		}).result
			.then(() => this.leaveAndCloseWebcast(), noop);
	}

	public closeRightSidebar(): void {
		this.webcast.layout.isRightSidebarOpen = false;
	}

	public onToggleSidebar(event: ISidebarToggleEvent): void {
		this.webcast.layout.isRightSidebarOpen = event.isOpen;
		if (this.activeSidebarButtonId && event.activeSidebarButtonId !== this.activeSidebarButtonId) {
			const priorTabNotification = this.getSidebarNotification(this.activeSidebarButtonId);
			if(priorTabNotification) {
				priorTabNotification.enabled = true;
			}
		}

		if(event.isOpen) {
			const notification = this.getSidebarNotification(event.activeSidebarButtonId);
			if (notification) {
				notification.enabled = false;
				notification.showIcon = false;
			}
		}
		this.activeSidebarButtonId = event.activeSidebarButtonId;
	}

	private initialize(): void {
		this.pushSubscription = this.getPushSubscription().subscribe();
		this.viewContext = {
			showNavBar: true,
			showPresentationSlides: true,
			infoSidebar: {
				showSharingUrl: true,
				showPPDownload: true,
				showHostName: false
			},
			titleCase: true,
			...this.webcastViewContext
		};

		this.translationKeys = this.translationKeys || TRANSLATION_KEYS; //fallback to rev strings.
		this.status = { active: true };

	}

	public closeWebcast(): void {
		this.$state.go(WEBCAST_LANDING_STATE, { id: this.webcast.id, blockAutoJoin: true });
	}

	private leaveAndCloseWebcast(discard: boolean = false): void {
		this.status = { processing: true };
		this.WebcastService.leave(this.webcast, discard)
			.then(() => this.onWebcastStopped())
			.catch(() => this.status = { error: true });
	}

	private onWebcastStopped(): void {
		this.removeNavigationHandlers();
		this.closeWebcast();
	}

	private configureNavigationHandlers(): void {
		//Check later.
		// this.locationChangeSub = this.RxWindowLocation.locationChangeStart$.subscribe(data => {
		// 	if (this.canLeave('', data.newUrl)){
		// 		this.leave();
		// 	} else {
		// 		data.event.preventDefault();
		// 	}
		// });

		this.transitionStartHandler = this.$transitions.onStart({}, transition => {
			if (this.isChildState(transition.to().name)) {
				return;
			}

			if (this.canLeave(transition.to().name)) {
				this.leave();
			} else {
				return false;
			}
		}) as () => void;

		$(window).on('beforeunload', this.confirmUnload);
	}

	private readonly confirmUnload = () => {
		if(this.producerView?.isReloading) {
			return;
		}

		if(this.webcast.viewerIdEnabled && this.webcast.videoOverlays?.some(v => v.type === VIDEO_OVERLAY_TYPE.VIEWER_ID)) {
			return;
		}

		if(this.webcast.webcastStatus.isEventAdminConnected &&
			!this.webcast.currentUser.isSinglePresenter) { //todo: support confirm close for presenter.
			return this.getNavMsg();
		}
	};

	private canLeave(toStateName: string, toHref: string = ''): boolean {
		return !this.webcast.webcastStatus.isEventAdminConnected ||
			this.webcast.currentUser.isRemovedFromWebcast ||
			this.webcast.currentUser.isSinglePresenter ||
			this.LoginRedirectService.isLoginState(toStateName) ||
			this.LoginRedirectService.isLoginHref(toHref) ||
			confirm(this.getNavMsg());
	}

	private leave(): void {
		this.removeNavigationHandlers();
	}

	private removeNavigationHandlers(): void {
		this.locationChangeSub?.unsubscribe();
		this.transitionStartHandler?.();
		this.confirmUnload && $(window).off('beforeunload', this.confirmUnload);
	}

	private getNavMsg(): string {
		return this.translateService.instant(this.webcast.currentUser.isEventAdmin ? 'Event_EventAdminDisconnectWarning' : 'Event_LeaveWebcastWarning');
	}

	private getPushSubscription(): Observable<void> {
		return this.webcast.currentUser.isEventAdmin$.pipe(
			switchMap(isAdmin => {
				return new Observable<void>(() => isAdmin ? this.subscribeEventAdminPush() : this.subscribePush());
			})
		);
	}

	private subscribeEventAdminPush(): IUnsubscribe {
		return this.PushBus.composeUnsubscribeFn([
			this.subscribePush(),

			this.webcast.presentationProfileDeviceId &&
				this.PushBus.subscribe(this.webcast.presentationProfileDeviceId, 'Webcast.EventAdmin', {
					DeviceOffline: () => {
						if (this.webcast.startingVideoSource) {
							this.webcast.webcastStatus.isCancelled = true;
							this.onWebcastStopped();
						}
					}
				}),
		]);
	}

	private subscribePush(): IUnsubscribe {
		const onEnded = eventData => {
			if(this.webcast.currentRun.isPreProduction || this.webcast.currentUser.isEventAdminOrModerator) {
				this.onWebcastStopped();
			}

			const bannersAtEnd = this.webcast.bannerDetails?.banners?.filter(l => l.pushMethod === 'AtEnd');
			if (bannersAtEnd?.length && this.webcast.currentRun.runNumber === eventData.runNumber) {
				this.DialogService.getDialog('AtEndDialog').open({
					initialState: {
						banners: bannersAtEnd
					}
				});
			}
		};

		return this.PushBus.composeUnsubscribeFn([
			this.PushBus.subscribe(this.webcast.id, '', {

				ScheduledEventStopped: eventData => onEnded(eventData),

				WebcastEnded: eventData => {
					if (eventData.status === WebcastStatus.Restarting) {
						return;
					}
					onEnded(eventData);
				},

				EmbeddedContentLinkProcessed: eventData => {
					const processedIndex = this.webcast.embeddedContent.contentLinks.findIndex(c => c.name === eventData.contentLink.name);
					if (processedIndex > -1) {
						this.webcast.embeddedContent.contentLinks[processedIndex] = eventData.contentLink;
					} else {
						this.webcast.embeddedContent.contentLinks.push(eventData.contentLink);
					}
					this.updateLinkNotification(eventData.contentLink);
				},

				EmbeddedContentLinkUpdated: eventData => {
					const updatedIndex = this.webcast.embeddedContent.contentLinks.findIndex(c => c.id === eventData.contentLink.id);
					if(updatedIndex > -1) {
						this.webcast.embeddedContent.contentLinks[updatedIndex] = eventData.contentLink;
						this.updateLinkNotification(eventData.contentLink);
					}
				},

				EmbeddedContentLinkDeleted: eventData => {
					this.webcast.embeddedContent.contentLinks = this.webcast.embeddedContent.contentLinks.filter(c => c.id !== eventData.contentLinkId);
				},

				BannerAdded: eventData => {
					const banners = this.webcast.bannerDetails.banners.map(link => ({ ...link }));
					const processedIndex = banners.findIndex(c => c.name === eventData.contentLink.name);
					if (processedIndex > -1) {
						banners[processedIndex] = eventData.contentLink;
					} else {
						banners.push(eventData.contentLink);
					}
					this.webcast.update({ bannerDetails: { isEnabled: true, banners: banners } });
				},

				BannerUpdated: eventData => {
					const banners = this.webcast.bannerDetails.banners.map(link => ({ ...link }));
					const updatedIndex = banners.findIndex(c => c.id === eventData.contentLink.id);
					banners[updatedIndex] = eventData.contentLink;
					this.webcast.update({ bannerDetails: { isEnabled: true, banners: banners } });
				},

				BannerDeleted: eventData => {
					let banners = this.webcast.bannerDetails.banners.map(link => ({ ...link }));
					banners = banners.filter(c => c.id !== eventData.contentLinkId);
					this.webcast.update({ bannerDetails: { isEnabled: true, banners: banners } });
				},

			}),

			this.webcast.currentUser.sessionId && this.PushBus.subscribe(this.webcast.currentUser.sessionId, {
				WebcastAttendeeDisconnected: data => {
					if (data.webcastId === this.webcast.id) {
						this.webcast.currentUser.isSessionTerminated = true;
						this.UserContext.logOutUser();
					}
				}
			})

		].filter(Boolean));
	}

	private isChildState(stateName: string): boolean {
		return stateName.startsWith(WEBCAST_VIEW_STATE);
	}

	public get height(): string {
		// mobile height is not reliably conveyed by 100vh
		if (this.isSmallScreenSize) {
			const mainHeaderHeight: number = this.MainHeaderPosition.height || 0;
			const calculatedHeight: number = window.innerHeight - mainHeaderHeight;

			return calculatedHeight + 'px';
		}

		return this.MainHeaderPosition.height ? `calc(100vh - ${this.MainHeaderPosition.height}px)` : '100vh';
	}

	private getSidebarNotification(buttonType: string): SidebarNotification {
		return this.sidebarNotifications[buttonType] ?? this.embeddedContentNotifications[buttonType];
	}

	private removeSidebarNotificationEventHandler(): void {
		NOTIFICATIONS_SIDEBARS.forEach(button =>
			this.getSidebarNotification(button)?.removeAllListeners(WEBCAST_SIDEBAR_NOTIFICATION_EVENT)
		);
	}


	private updateAdminHeader(): void {
		this.statusClass = 'processing';

		if(this.webcast.broadcast.isStarting) {
			this.statusMsg = this.translateService.instant('Event_StartingBroadcast');
		}
		else if(this.webcast.broadcast.isStopping) {
			this.statusMsg = this.translateService.instant('Event_StoppingBroadcast');
		}
		else if(this.webcast.broadcast.isActive) {
			this.statusClass = 'theme-accent theme-accent-font-border';
			this.statusMsg = this.translateService.instant(
				this.webcast.isVodSource ? 'Event_AttendeesCanSeeEvent_Vod' :
				this.webcast.isWebrtcSinglePresenter ? 'Event_AttendeesCanSeeEvent_Presenter' :
				'Event_AttendeesCanSeeEvent'
			);

			if(this.webcast.isWebrtcSinglePresenter && !this.webcast.currentUser.isSinglePresenter) {
				this.statusMsg = this.translateService.instant(this.webcast.isPresenterReady ?
					'Event_PresenterIsLive' :
					'Event_PresenterNotReady') + ' ' + this.statusMsg;

			}

		}
		else if(this.webcast.webcastStatus.isCancelled) {
			if(!this.webcast.enableLiveStreamStatusDisplay) {
				this.statusMsg = `${this.translateService.instant('Event_UnableToStartWebcast')} ${this.translateService.instant('Event_UnableToStartVideoSource')} ${this.translateService.instant('DeviceOffline')}`;
			}
		}
		else if(this.webcast.isAutomated && !this.webcast.webcastStatus.isEnded) {
			this.statusClass = 'warning';
			this.statusMsg = this.translateService.instant(this.webcast.isVodSource ? 'Event_AutomatedWebcast_VodStatus' : 'Event_StartingBroadcast');
		}
		else {
			this.statusClass = 'warning';
			this.statusMsg = this.translateService.instant(
				this.webcast.isVodSource ? 'Event_AttendeesCannotSeeEvent_Vod' :
				this.webcast.isWebrtcSinglePresenter ? 'Event_AttendeesCannotSeeEvent_Presenter' :
				'Event_AttendeesCannotSeeEvent');

			if(this.webcast.isWebrtcSinglePresenter && !this.webcast.currentUser.isSinglePresenter) {
				this.statusMsg = this.translateService.instant(this.webcast.isPresenterReady ?
					'Event_PresenterIsReady' :
					'Event_PresenterNotReady') + ' ' + this.statusMsg;

			}
		}
	}

	private initVideoOverlays(): void {
		const viewIdOverlaySubscription = this.webcast.webcast$
			.pipe(
				filter((webcast: WebcastModel) => !!webcast.playbacks && !this.webcast.videoOverlays?.some(o => o.type === VIDEO_OVERLAY_TYPE.VIEWER_ID)),
				pluck('viewerIdEnabled'),
				distinctUntilChanged(),
				map(() => {
					return this.webcast.constructViewerIdOverlay();
				})
			).subscribe({
				next: overlay => {
					if (overlay) {
						this.webcast.addVideoOverlay(overlay);
					} else {
						this.webcast.removeVideoOverlay(VIDEO_OVERLAY_TYPE.VIEWER_ID);
					}
				},
				error: e => console.error('Error while constructing viewIdOverlaySubscription overlay: ', e)
			});

		this.rxjsSubscriptions.push(
			viewIdOverlaySubscription
		);
	}

	public saveEdits(): void {
		this.sidebar?.editWebcastComponent?.editWebcastForm?.submit();
	}

	public tryInitEditWebcast(): void {
		const form = this.sidebar?.editWebcastComponent?.editWebcastForm?.form;
		this.editWebcastStatusSub = form?.statusChanges?.subscribe(() => {
			this.editWebcastInvalid = form.invalid;
		});
	}

	public onRestartWebcastSource(customMsg: string): void {
		this.WebcastService.restartWebcastVideoSource(this.webcast.id, this.webcast.currentRun.runNumber, customMsg)
			.catch(err => {
				console.error('Error while restarting webcast source', err);
			});
	}

	public onSwitchStream(): void {
		this.WebcastService.switchWebcastStream(this.webcast.id, this.webcast.currentRun.runNumber)
			.catch(err => {
				console.error('Error while switching stream', err);
			});
	}

	private updateLinkNotification(contentLink: IEmbeddedContentLink): void {
		if(contentLink.isEnabled) {
			const notification = new SidebarNotification();
			notification.showIcon = true;
			this.embeddedContentNotifications[contentLink.id] = notification;
		}
	}
}
